// Markdown formatting commands for the editor toolbar and shortcuts. All commands are pure
// CodeMirror transactions (StateCommand), operate on every selection range and toggle off when
// the formatting is already applied.
import { syntaxTree } from "@codemirror/language";
import {
  EditorSelection,
  type ChangeSpec,
  type EditorState,
  type SelectionRange,
  type StateCommand,
  type Transaction,
} from "@codemirror/state";
import type { SyntaxNode } from "@lezer/common";

type Dispatch = (tr: Transaction) => void;

// ---------------------------------------------------------------------------
// Inline formatting

export type InlineKind = "bold" | "italic" | "strike" | "code";

const INLINE: Record<InlineKind, { marker: string; node: string; mark: string }> = {
  bold: { marker: "**", node: "StrongEmphasis", mark: "EmphasisMark" },
  italic: { marker: "*", node: "Emphasis", mark: "EmphasisMark" },
  strike: { marker: "~~", node: "Strikethrough", mark: "StrikethroughMark" },
  code: { marker: "`", node: "InlineCode", mark: "CodeMark" },
};

/** Innermost ancestor (or self) named `name` that contains [from, to]. */
function enclosingNode(state: EditorState, from: number, to: number, name: string): SyntaxNode | null {
  const tree = syntaxTree(state);
  for (const side of [1, -1] as const) {
    let node: SyntaxNode | null = tree.resolveInner(from, side);
    for (; node; node = node.parent) {
      if (node.name === name && node.from <= from && node.to >= to) return node;
    }
  }
  return null;
}

function markChildren(node: SyntaxNode, mark: string): SyntaxNode[] {
  const out: SyntaxNode[] = [];
  for (let c = node.firstChild; c; c = c.nextSibling) if (c.name === mark) out.push(c);
  return out;
}

/** Text-only check: is the range directly surrounded by `marker` (and not a longer run of it)? */
function surroundedBy(state: EditorState, from: number, to: number, marker: string): boolean {
  const m = marker.length;
  if (from < m || to + m > state.doc.length) return false;
  if (state.sliceDoc(from - m, from) !== marker || state.sliceDoc(to, to + m) !== marker) return false;
  if (marker === "*") {
    // "**x**" is bold, not italic – unless it is "***x***".
    const before = state.sliceDoc(from - 2, from - 1);
    const after = state.sliceDoc(to + 1, to + 2);
    if ((before === "*") !== (after === "*")) return false;
    if (before === "*" && state.sliceDoc(from - 3, from - 2) !== "*") return false;
  }
  return true;
}

function wordRange(state: EditorState, pos: number): { from: number; to: number } | null {
  const w = state.wordAt(pos);
  if (!w || w.from === w.to) return null;
  if (pos === w.from || pos === w.to) return null; // only when the cursor is inside the word
  return { from: w.from, to: w.to };
}

export function toggleInline(kind: InlineKind): StateCommand {
  const { marker, node: nodeName, mark } = INLINE[kind];
  const m = marker.length;
  return ({ state, dispatch }) => {
    const tr = state.changeByRange((range) => {
      const { from, to } = range;
      // 1) Inside an existing node: remove its markers.
      const node = enclosingNode(state, from, to, nodeName);
      if (node) {
        const marks = markChildren(node, mark);
        if (marks.length >= 2) {
          const open = marks[0];
          const close = marks[marks.length - 1];
          const changes: ChangeSpec[] = [
            { from: open.from, to: open.to },
            { from: close.from, to: close.to },
          ];
          const openLen = open.to - open.from;
          const map = (p: number) => {
            if (p <= open.from) return p;
            if (p <= open.to) return open.from;
            if (p <= close.from) return p - openLen;
            if (p <= close.to) return close.from - openLen;
            return p - openLen - (close.to - close.from);
          };
          return { changes, range: EditorSelection.range(map(range.anchor), map(range.head)) };
        }
      }
      // 2) Selection is directly wrapped by markers: unwrap.
      if (surroundedBy(state, from, to, marker)) {
        return {
          changes: [
            { from: from - m, to: from },
            { from: to, to: to + m },
          ],
          range: EditorSelection.range(from - m, to - m),
        };
      }
      // 3) Selection includes the markers itself.
      const text = state.sliceDoc(from, to);
      let selfWrapped = text.length > 2 * m && text.startsWith(marker) && text.endsWith(marker);
      if (selfWrapped && marker === "*") {
        const b2 = text.startsWith("**");
        const e2 = text.endsWith("**");
        if (b2 !== e2 || (b2 && !text.startsWith("***"))) selfWrapped = false;
      }
      if (selfWrapped) {
        const inner = text.slice(m, -m);
        return { changes: { from, to, insert: inner }, range: EditorSelection.range(from, from + inner.length) };
      }
      // 4) Wrap (empty cursor inside a word → wrap the word).
      let f = from;
      let t = to;
      if (range.empty) {
        const w = wordRange(state, from);
        if (w) {
          f = w.from;
          t = w.to;
        }
      }
      if (f === t) {
        return {
          changes: { from: f, insert: marker + marker },
          range: EditorSelection.cursor(f + m),
        };
      }
      const changes: ChangeSpec[] = [
        { from: f, insert: marker },
        { from: t, insert: marker },
      ];
      if (range.empty) return { changes, range: EditorSelection.cursor(from + m) };
      return { changes, range: EditorSelection.range(f + m, t + m) };
    });
    dispatch(state.update(tr, { scrollIntoView: true, userEvent: "input.format" }));
    return true;
  };
}

export const toggleBold = toggleInline("bold");
export const toggleItalic = toggleInline("italic");
export const toggleStrike = toggleInline("strike");
export const toggleInlineCode = toggleInline("code");

// ---------------------------------------------------------------------------
// Line based formatting

/** Parsed list/quote/heading prefix of a line. */
export interface LinePrefix {
  indent: string;
  quote: string; // "> " prefix(es) after the indent
  heading: number; // 0 = none
  list: "bullet" | "ordered" | null;
  marker: string; // e.g. "- ", "1. "
  number: number | null;
  task: "open" | "done" | null;
  box: string; // "[ ] " incl. trailing space
  /** Offset (within the line) where the content after all prefixes starts. */
  contentFrom: number;
}

const LINE_RE = /^(\s*)((?:>\s?)*)(#{1,6}\s+)?(?:([-*+]|\d{1,9}[.)])(\s+|$))?(\[([ xX])\](?:\s+|$))?/;

export function parseLinePrefix(text: string): LinePrefix {
  const m = LINE_RE.exec(text)!;
  const indent = m[1] ?? "";
  const quote = m[2] ?? "";
  const headingMark = m[3] ?? "";
  if (headingMark) {
    return {
      indent,
      quote,
      heading: headingMark.trim().length,
      list: null,
      marker: "",
      number: null,
      task: null,
      box: "",
      contentFrom: indent.length + quote.length + headingMark.length,
    };
  }
  const listMark = m[4];
  const marker = listMark ? listMark + (m[5] ?? "") : "";
  const list: LinePrefix["list"] = listMark ? (/^\d/.test(listMark) ? "ordered" : "bullet") : null;
  const box = list ? (m[6] ?? "") : ""; // a checkbox without list marker is not a task
  return {
    indent,
    quote,
    heading: 0,
    list,
    marker,
    number: list === "ordered" ? parseInt(listMark!, 10) : null,
    task: box ? (m[7] === " " ? "open" : "done") : null,
    box,
    contentFrom: indent.length + quote.length + marker.length + box.length,
  };
}

/** Lines touched by the selection ranges, in document order, deduplicated. */
function selectedLines(state: EditorState): { number: number; from: number; to: number; text: string }[] {
  const seen = new Set<number>();
  const out: { number: number; from: number; to: number; text: string }[] = [];
  for (const r of state.selection.ranges) {
    const first = state.doc.lineAt(r.from).number;
    let last = state.doc.lineAt(r.to).number;
    // A selection ending at the start of a line does not include that line.
    if (last > first && state.doc.line(last).from === r.to) last--;
    for (let n = first; n <= last; n++) {
      if (seen.has(n)) continue;
      seen.add(n);
      const l = state.doc.line(n);
      out.push({ number: n, from: l.from, to: l.to, text: l.text });
    }
  }
  return out.sort((a, b) => a.number - b.number);
}

function targetLines(state: EditorState) {
  const lines = selectedLines(state);
  // Skip blank lines when several lines are selected.
  return lines.length > 1 ? lines.filter((l) => l.text.trim() !== "") : lines;
}

function applyLineChanges(state: EditorState, dispatch: Dispatch, changes: ChangeSpec[], userEvent = "input.format") {
  if (changes.length === 0) return true;
  const cs = state.changes(changes);
  // Keep multi-line selections covering whole lines: a non-empty range starting at a line start
  // stays at the (new) line start instead of moving behind the inserted prefix.
  const selection = EditorSelection.create(
    state.selection.ranges.map((r) => {
      if (r.empty) return EditorSelection.cursor(cs.mapPos(r.head, 1));
      const startAssoc = state.doc.lineAt(r.from).from === r.from ? -1 : 1;
      const from = cs.mapPos(r.from, startAssoc);
      const to = cs.mapPos(r.to, 1);
      return r.anchor <= r.head ? EditorSelection.range(from, to) : EditorSelection.range(to, from);
    }),
    state.selection.mainIndex,
  );
  dispatch(state.update({ changes: cs, selection, scrollIntoView: true, userEvent }));
  return true;
}

/** Replace the list part (marker + box) of a line. */
function replaceListPart(line: { from: number; text: string }, p: LinePrefix, insert: string): ChangeSpec {
  const start = line.from + p.indent.length + p.quote.length;
  const end = start + p.marker.length + p.box.length;
  return { from: start, to: end, insert };
}

export const toggleBulletList: StateCommand = ({ state, dispatch }) => {
  const lines = targetLines(state);
  const parsed = lines.map((l) => parseLinePrefix(l.text));
  const allBullet = parsed.every((p) => p.list === "bullet" && !p.task);
  const changes = lines.map((l, i) => {
    const p = parsed[i];
    if (allBullet) return replaceListPart(l, p, "");
    if (p.heading) {
      const start = l.from + p.indent.length + p.quote.length;
      return { from: start, to: l.from + p.contentFrom, insert: "- " };
    }
    return replaceListPart(l, p, "- ");
  });
  return applyLineChanges(state, dispatch, changes);
};

export const toggleOrderedList: StateCommand = ({ state, dispatch }) => {
  const lines = targetLines(state);
  const parsed = lines.map((l) => parseLinePrefix(l.text));
  const allOrdered = parsed.every((p) => p.list === "ordered");
  let n = 1;
  // Continue numbering of a directly preceding ordered item with the same indentation.
  if (lines.length && lines[0].number > 1) {
    const prev = parseLinePrefix(state.doc.line(lines[0].number - 1).text);
    if (prev.list === "ordered" && prev.indent === parsed[0].indent && prev.number !== null) n = prev.number + 1;
  }
  const changes = lines.map((l, i) => {
    const p = parsed[i];
    if (allOrdered) return replaceListPart(l, p, "");
    const insert = `${n++}. `;
    if (p.heading) {
      const start = l.from + p.indent.length + p.quote.length;
      return { from: start, to: l.from + p.contentFrom, insert };
    }
    return replaceListPart(l, p, insert);
  });
  return applyLineChanges(state, dispatch, changes);
};

/**
 * Task toggle: lines without a checkbox get `- [ ] `; when every target line already is a task,
 * their done state is toggled instead (all done if any is open).
 */
export const toggleTaskList: StateCommand = ({ state, dispatch }) => {
  const lines = targetLines(state);
  const parsed = lines.map((l) => parseLinePrefix(l.text));
  const allTasks = parsed.every((p) => p.task !== null);
  if (allTasks) {
    const anyOpen = parsed.some((p) => p.task === "open");
    const changes = lines.map((l, i) => {
      const p = parsed[i];
      const boxPos = l.from + p.indent.length + p.quote.length + p.marker.length + 1;
      return { from: boxPos, to: boxPos + 1, insert: anyOpen ? "x" : " " };
    });
    return applyLineChanges(state, dispatch, changes, "input.toggle");
  }
  const changes: ChangeSpec[] = [];
  lines.forEach((l, i) => {
    const p = parsed[i];
    if (p.task) return;
    if (p.heading) {
      const start = l.from + p.indent.length + p.quote.length;
      changes.push({ from: start, to: l.from + p.contentFrom, insert: "- [ ] " });
      return;
    }
    const marker = p.list === "ordered" ? p.marker : "- ";
    changes.push(replaceListPart(l, p, `${marker}[ ] `));
  });
  return applyLineChanges(state, dispatch, changes);
};

export const toggleQuote: StateCommand = ({ state, dispatch }) => {
  const lines = targetLines(state);
  const allQuoted = lines.every((l) => /^\s*>/.test(l.text));
  const changes = lines.map((l) => {
    const p = parseLinePrefix(l.text);
    if (allQuoted) {
      const m = /^(\s*)>\s?/.exec(l.text)!;
      return { from: l.from + m[1].length, to: l.from + m[0].length, insert: "" };
    }
    return { from: l.from + p.indent.length, insert: "> " };
  });
  return applyLineChanges(state, dispatch, changes);
};

/** Set heading level (0 = paragraph). Applying the current level again removes it. */
export function setHeading(level: number): StateCommand {
  return ({ state, dispatch }) => {
    const lines = targetLines(state);
    const parsed = lines.map((l) => parseLinePrefix(l.text));
    const allSame = level > 0 && parsed.every((p) => p.heading === level);
    const changes = lines.map((l, i) => {
      const p = parsed[i];
      const start = l.from + p.indent.length + p.quote.length;
      const end = p.heading ? l.from + p.contentFrom : start + p.marker.length + p.box.length;
      const insert = allSame || level === 0 ? "" : "#".repeat(level) + " ";
      return { from: start, to: end, insert };
    });
    return applyLineChanges(state, dispatch, changes);
  };
}

/** paragraph → H1 → H2 → H3 → paragraph (based on the first selected line). */
export const cycleHeading: StateCommand = (target) => {
  const first = target.state.doc.lineAt(target.state.selection.main.from);
  const cur = parseLinePrefix(first.text).heading;
  const next = cur >= 3 ? 0 : cur + 1;
  return setHeading(next)(target);
};

// ---------------------------------------------------------------------------
// Indentation for lists

function listIndentUnit(p: LinePrefix): string {
  return p.list === "ordered" ? " ".repeat(Math.max(2, p.marker.length)) : "  ";
}

/** Indent list lines (Tab). Returns false when no selected line is a list item. */
export const indentList: StateCommand = ({ state, dispatch }) => {
  const lines = selectedLines(state);
  const parsed = lines.map((l) => parseLinePrefix(l.text));
  if (!parsed.some((p) => p.list)) return false;
  const changes: ChangeSpec[] = [];
  lines.forEach((l, i) => {
    if (parsed[i].list) changes.push({ from: l.from, insert: listIndentUnit(parsed[i]) });
  });
  return applyLineChanges(state, dispatch, changes, "input.indent");
};

/** Outdent list lines (Shift+Tab). Returns false when no selected line is a list item. */
export const outdentList: StateCommand = ({ state, dispatch }) => {
  const lines = selectedLines(state);
  const parsed = lines.map((l) => parseLinePrefix(l.text));
  if (!parsed.some((p) => p.list)) return false;
  const changes: ChangeSpec[] = [];
  lines.forEach((l, i) => {
    const p = parsed[i];
    if (!p.list) return;
    const remove = Math.min(listIndentUnit(p).length, p.indent.length);
    if (remove > 0) changes.push({ from: l.from, to: l.from + remove });
  });
  return applyLineChanges(state, dispatch, changes, "delete.dedent");
};

// ---------------------------------------------------------------------------
// Blocks & inserts

export const toggleCodeBlock: StateCommand = ({ state, dispatch }) => {
  const main = state.selection.main;
  const fenced = enclosingNode(state, main.from, main.to, "FencedCode");
  if (fenced) {
    const open = state.doc.lineAt(fenced.from);
    const close = state.doc.lineAt(fenced.to);
    const changes: ChangeSpec[] = [];
    const hasClose = close.number > open.number && /^\s*(```|~~~)/.test(close.text);
    if (hasClose && close.number === open.number + 1) {
      changes.push({ from: open.from, to: close.to });
    } else {
      changes.push({ from: open.from, to: Math.min(open.to + 1, state.doc.length) });
      if (hasClose) changes.push({ from: close.from - 1, to: close.to });
    }
    return applyLineChanges(state, dispatch, changes);
  }
  const tr = state.changeByRange((range) => {
    const a = state.doc.lineAt(range.from);
    const b = state.doc.lineAt(range.to);
    if (range.empty && a.text.trim() === "") {
      return {
        changes: { from: a.from, to: a.to, insert: "```\n\n```" },
        range: EditorSelection.cursor(a.from + 4),
      };
    }
    const changes: ChangeSpec[] = [
      { from: a.from, insert: "```\n" },
      { from: b.to, insert: "\n```" },
    ];
    return { changes, range: EditorSelection.range(range.anchor + 4, range.head + 4) };
  });
  dispatch(state.update(tr, { scrollIntoView: true, userEvent: "input.format" }));
  return true;
};

export const insertLink: StateCommand = ({ state, dispatch }) => {
  const tr = state.changeByRange((range) => {
    const link = enclosingNode(state, range.from, range.to, "Link");
    if (link) {
      const marks = markChildren(link, "LinkMark");
      if (marks.length >= 2) {
        const textFrom = marks[0].to;
        const textTo = marks[1].from;
        const text = state.sliceDoc(textFrom, textTo);
        return {
          changes: { from: link.from, to: link.to, insert: text },
          range: EditorSelection.range(link.from, link.from + text.length),
        };
      }
    }
    const sel = state.sliceDoc(range.from, range.to);
    const isUrl = /^https?:\/\/\S+$/.test(sel);
    if (isUrl) {
      const insert = `[](${sel})`;
      return { changes: { from: range.from, to: range.to, insert }, range: EditorSelection.cursor(range.from + 1) };
    }
    const insert = `[${sel}](url)`;
    const urlFrom = range.from + sel.length + 3;
    return {
      changes: { from: range.from, to: range.to, insert },
      range: EditorSelection.range(urlFrom, urlFrom + 3),
    };
  });
  dispatch(state.update(tr, { scrollIntoView: true, userEvent: "input.format" }));
  return true;
};

/** Insert a block (lines) after the current line, separated by blank lines as needed. */
function insertBlock(state: EditorState, range: SelectionRange, block: string, selectFrom: number, selectTo: number) {
  const line = state.doc.lineAt(range.head);
  if (line.text.trim() === "") {
    const needAfter = line.number < state.doc.lines && state.doc.line(line.number + 1).text.trim() !== "";
    const insert = block + (needAfter ? "\n" : "");
    return {
      changes: { from: line.from, to: line.to, insert },
      range: EditorSelection.range(line.from + selectFrom, line.from + selectTo),
    };
  }
  const prefix = "\n\n";
  const insert = prefix + block;
  const base = line.to + prefix.length;
  return {
    changes: { from: line.to, insert },
    range: EditorSelection.range(base + selectFrom, base + selectTo),
  };
}

export const insertHorizontalRule: StateCommand = ({ state, dispatch }) => {
  const tr = state.changeByRange((range) => {
    const block = "---\n";
    return insertBlock(state, range, block, block.length, block.length);
  });
  dispatch(state.update(tr, { scrollIntoView: true, userEvent: "input.format" }));
  return true;
};

export const TABLE_TEMPLATE = [
  "| Column 1 | Column 2 | Column 3 |",
  "| --- | --- | --- |",
  "|  |  |  |",
  "|  |  |  |",
].join("\n");

export const insertTable: StateCommand = ({ state, dispatch }) => {
  const tr = state.changeByRange((range) => insertBlock(state, range, TABLE_TEMPLATE, 2, 10));
  dispatch(state.update(tr, { scrollIntoView: true, userEvent: "input.format" }));
  return true;
};

// ---------------------------------------------------------------------------
// Active state for the toolbar

export type FormatName =
  | InlineKind
  | "link"
  | "codeblock"
  | "h1"
  | "h2"
  | "h3"
  | "bullet"
  | "ordered"
  | "task"
  | "quote";

export function activeFormats(state: EditorState): Set<FormatName> {
  const out = new Set<FormatName>();
  const { from, to } = state.selection.main;
  for (const kind of Object.keys(INLINE) as InlineKind[]) {
    if (enclosingNode(state, from, to, INLINE[kind].node)) out.add(kind);
  }
  if (enclosingNode(state, from, to, "Link")) out.add("link");
  if (enclosingNode(state, from, to, "FencedCode")) out.add("codeblock");
  const p = parseLinePrefix(state.doc.lineAt(from).text);
  if (p.heading >= 1 && p.heading <= 3) out.add(`h${p.heading}` as FormatName);
  if (p.task) out.add("task");
  else if (p.list === "bullet") out.add("bullet");
  else if (p.list === "ordered") out.add("ordered");
  if (p.quote) out.add("quote");
  return out;
}

/** Heading level of the line with the main cursor (0 = paragraph). */
export function currentHeading(state: EditorState): number {
  return parseLinePrefix(state.doc.lineAt(state.selection.main.from).text).heading;
}
